import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import "../../../assets/custom-css/curriculum.css";
import CustomButton from "../../../components/shared/button/CustomButton";
import CommonTable from "../../../components/shared/table/Table";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";
import { PlusOutlined, ExportOutlined } from "@ant-design/icons";
import CustomBreadcrumb from "../../../components/shared/breadcrumbs/CustomBreadCrumb";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { CurriculumHistoryColumns } from "../../../assets/FormFeilds/CurriculumField";
import { useBundleHistory } from "../../../assets/hooks/CurriculumHooks/useBundleHistory";
import { deleteCourseFromBundle } from "../../../service/BundleService";
import Back from "../../../assets/back-btn.svg";
import ConfirmDeletePopup from "../../../components/shared/confirmDeletePopup/ConfirmDeletePopup";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../components/shared/pagination/messages";
import AddCourseModal from "../../../components/admin/Curriculum/AddCourseModal";
import PageLoader from "../../../components/global/Loader/PageLoader";
import CustomInput from "../../../components/shared/Input/Custom-Input";
import searchIcon from "../../../assets/Search-Icon.svg";

const CurriculumHistory = () => {
  const tableRef = React.useRef(null);
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const { id } = useParams();
  const location = useLocation();
  const bundleName = location.state?.bundleName || "";
  const isActive = location.state?.isActive ?? true;
  const [deleteCourse, setDeleteCourse] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [visiblePopover, setVisiblePopover] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [deleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [searchText, setSearchText] = useState("");

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    totalElements,
    setTotalElements,
    setPageSize,
  } = usePagination();

  const { courseList, getBundlesHistory } = useBundleHistory();

  const fetchBundleHistory = useCallback(async () => {
    setLoading(true);
    try {
      const query = searchText?.trim() || "";
      const response = await getBundlesHistory(
        id,
        currentPage - 1,
        pageSize,
        "createdAt",
        "asc",
        query, 
        isActive
      );
      if (response) {
        setTotalElements(response.totalElements);
      }
    } catch (error) {
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [id, currentPage, pageSize, isActive, searchText, getBundlesHistory, setTotalElements]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBundleHistory();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchBundleHistory]);

  const handleViewUserClick = (id, name) => {
    navigate(`/books/course-content/${id}?name=${encodeURIComponent(name)}`);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleAddNew = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleOpenConfirmDeletePopup = (course) => {
    setIsConfirmPopupOpen(true);
    setDeleteCourse(course);
  };


  const handleDeleteCourse = async () => {
    setIsDeleting(true);
    try {
      const data = await deleteCourseFromBundle(id, deleteCourse.id);
      showSuccessMessage(data?.message || "Course Removed successfully!");
      fetchBundleHistory();
    } catch (error) {
      showErrorMessage(
        error?.message || "Error occurred while deleting the Course.",
      );
    } finally {
      setIsDeleting(false);
      setIsConfirmPopupOpen(false);
      setDeleteCourse(null);
    }
  };

  const handleTableChange = (pagination) => {
    if (pagination) {
      if (pagination.pageSize !== pageSize) {
        setPageSize(pagination.pageSize);
        setCurrentPage(1);
      } else {
        setCurrentPage(pagination.current);
      }
      const scrollableNode = tableRef.current?.querySelector(".ant-table-body");
      if (scrollableNode) {
        scrollableNode.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  };

  const handleSearch = (query) => {
    setSearchText(query);
    if (query && currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  return (
    <>
      <PageLoader visible={showLoader || deleting} />
      <div className="p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)] gap-6">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <h1 className="text-[#e74860] text-[1.75rem] font-normal">
              {`${bundleName} Details`}
            </h1>
            <div className="flex gap-[10px] justify-end">
              <CustomInput
                icon={
                  <img src={searchIcon} alt="search" className="w-6 h-6" />
                }
                placeholder="Search"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                className="!w-[280px]"
              />
              {isActive && (
                <CustomButton
                  icon={<PlusOutlined />}
                  onClick={handleAddNew}
                  variant="primary"
                >
                  Add Courses
                </CustomButton>
              )}
            </div>
          </div>

          <div className="w-full" ref={tableRef}>
            <CommonTable
              data={courseList}
              scroll={{ y: "60vh" }}
              columns={CurriculumHistoryColumns(
                visiblePopover,
                setVisiblePopover,
                hoveredButton,
                setHoveredButton,
                handleOpenConfirmDeletePopup,
                handleViewUserClick,
              )}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalElements,
                showSizeChanger: true,
              }}
              showLoader={loading}
              onChange={handleTableChange}
              serverSide={true}
            />
          </div>
        </div>

        <ConfirmDeletePopup
          isOpen={isConfirmPopupOpen}
          onClose={() => setIsConfirmPopupOpen(false)}
          onConfirm={handleDeleteCourse}
        />

        <AddCourseModal
          isModalOpen={isModalOpen}
          handleCloseModal={handleCloseModal}
          setLoading={setShowLoader}
          bundleId={id}
          getCourses={fetchBundleHistory}
        />
      </div>
    </>
  );
};

export default AdminHOC(CurriculumHistory);
